import React from 'react';
import LegalLayout from '../../components/LegalLayout';
import LegalDocBody from '../../components/LegalDocBody';

const SOURCE = `NoFee Commission-Free Restaurant Ordering Platform Rewards Program Terms

How points and gift credits are earned, redeemed, and managed on NoFee

These Rewards Program Terms govern the NoFee Rewards program. They supplement the Terms of Service and Customer Agreement. By participating in Rewards, you agree to these Terms.

Effective date: [[CONFIRM: insert date]]

1. THE PROGRAM

1.1 NoFee Rewards lets eligible customers earn points for certain activities and convert points into gift credits that can be applied toward Orders at participating Restaurants on the Platform. The program is operated by NoFee and is offered for personal, non-commercial use.

1.2 Participation is voluntary, free, and limited to customers with an Account in good standing who comply with these Terms, the Terms of Service, and the Customer Agreement.

2. DEFINITIONS

Points
Units earned for qualifying activities. Points have no independent monetary value and are not property.

Gift Credit
Account credit, denominated in U.S. dollars, obtained by converting Points, redeemable only toward Orders on the Platform. Gift Credit has no cash value and is not redeemable for cash.

Qualifying Activity
An activity NoFee designates as eligible to earn Points, as listed in Section 3.

Verified Share
A social share that NoFee's systems confirm was actually sent or posted by you and that leads to a genuine sign-up or Order, as described in Section 3.3.

3. EARNING POINTS

3.1 You earn Points for Qualifying Activities at the rates NoFee publishes. The current earning schedule is set out below; NoFee maintains a single canonical schedule, and the figures shown in the app control if a discrepancy ever appears.

3.2 Earning schedule ([[CONFIRM: INSERT FINAL VALUES]]):

| Qualifying activity | Points | Notes |
| Placing an Order |  | Earned when the Order is completed. |
| Sign-up bonus |  | One-time, after account creation/activation. |
| Verified on-time delivery confirmation |  | For confirming an eligible Order. |
| Leaving a review |  | Subject to the incentivized-review disclosure in 3.4. |
| Referring a friend who signs up |  | Friend must create a genuine account. |
| Referring a Restaurant that joins |  | Unlocks after the Restaurant completes onboarding and its first Subscription payment is collected. |
| Verified social share |  | Per Section 3.3; clicking a share button alone does not earn Points. |

3.3 Verified sharing. Points for sharing are awarded only for Verified Shares — shares NoFee's systems confirm you actually sent or posted and that lead to genuine activity. Merely clicking a share button, or shares that NoFee detects as bot-driven, fake, duplicate, or self-referred, do not earn Points. NoFee does not post anything on your behalf.

3.4 Incentivized reviews. If Points are offered for reviews, the reward is given for leaving a review, not for leaving a positive review, and you must disclose that your review was incentivized where required. NoFee may withhold Points for reviews that are fake, paid-for-positivity, or that violate platform or FTC rules. [[CONFIRM: if this is hard to administer compliantly, consider removing review rewards entirely]]

3.5 NoFee may set per-activity, per-day, per-account, and per-period limits, and may require a minimum activity threshold, to protect the program. Points are awarded only after NoFee validates the underlying activity, and pending Points may be shown separately from available Points.

4. CONVERTING AND REDEEMING

4.1 Conversion. Points convert to Gift Credit at the published rate ([[CONFIRM: confirm rate; app shows 20 points = $1.00]]). Conversion or redemption requests may be subject to review.

4.2 Redemption review. Gift Credit redemption requests may be reviewed before the credit is issued or applied. Consistent with the Platform, [[CONFIRM: confirm review window; app states "reviewed within 72 hours"]]. NoFee aims to automate eligibility checks and reserve manual review for flagged activity.

4.3 Using Gift Credit. Gift Credit can be applied toward eligible Orders at participating Restaurants on the Platform. Gift Credit is not redeemable for cash, cannot be transferred or sold, cannot be combined except as the Platform allows, and is not redeemable outside the NoFee network.

4.4 Rewards balances are shown in your account (for example, available, pending review, redeemed, and lifetime earned). NoFee's records of Points and Gift Credit are final absent manifest error.

5. EXPIRATION

5.1 Points expiration. [[CONFIRM: state Points expiration, e.g., "Points expire 12 months after they are earned if the Account has no qualifying activity", or "Points do not expire while the Account is active"]].

5.2 Gift Credit expiration. Gift Credit [[CONFIRM: state whether and when Gift Credit expires; some states restrict expiration of stored value]]. Where law prohibits expiration of certain credits, those credits will not expire to the extent required.

5.3 On Account closure or termination for cause, unredeemed Points and Gift Credit are forfeited, except where law requires otherwise.

6. FRAUD PREVENTION AND ABUSE

6.1 The following are prohibited and will void affected Points and Gift Credit:
(a) creating multiple, duplicate, or fake accounts, or self-referrals;
(b) using bots, scripts, or automation to generate activity, shares, or referrals;
(c) fake or non-genuine Orders, reviews, or sign-ups, or "gaming" promised-time confirmations;
(d) purchasing, selling, transferring, or pooling Points or Gift Credit; and
(e) any activity that violates these Terms, the Terms of Service, program rules, or law.

6.2 Verification, withholding, and clawback. NoFee may review any activity and may withhold, delay, deny, reverse, or clawback Points and Gift Credit it reasonably believes are connected to fraud, abuse, error, or refunded/charged-back Orders, including after they are issued, and may offset against future balances.

7. SUSPENSION AND TERMINATION OF PARTICIPATION

7.1 NoFee may suspend or terminate your participation in Rewards, and may cancel Points and Gift Credit, if you violate these Terms, the Terms of Service, or program rules, engage in fraud or abuse, or if your Account is suspended or terminated. Suspension of Rewards may occur independently of, or together with, Account suspension under the Terms of Service.

7.2 NoFee may also suspend the program, or your participation, while investigating suspected abuse.

8. CHANGES TO THE PROGRAM

8.1 NoFee may modify, suspend, or discontinue the Rewards program, in whole or in part, and may change earning rates, conversion rates, redemption rules, limits, and expiration, at any time, prospectively, on reasonable notice (for example, by posting updated Terms or in-app notice). Changes do not retroactively remove Gift Credit already validly issued and available, except for clawbacks permitted under Section 6 or as required by law.

8.2 If NoFee discontinues the program, it will provide reasonable notice and, where practicable, a period to redeem available Gift Credit, subject to law.

9. GENERAL

9.1 Rewards have no cash value, are not property, and are not bank or money-transmission accounts. The program is void where prohibited. Tax consequences of Rewards, if any, are your responsibility.

9.2 Questions about Rewards can be sent to [[CONFIRM: rewards/support contact]]. These Terms are governed by the law and venue stated in the Terms of Service, and the Terms of Service's disclaimers and limitation of liability apply.
`;

const RewardsTerms = () => (
  <LegalLayout title="Rewards Program Terms" lastUpdated="June 25, 2026">
    <LegalDocBody source={SOURCE} />
  </LegalLayout>
);

export default RewardsTerms;
